import { useCallback, useEffect, useMemo, useRef } from "react"
import type { MutableRefObject } from "react"
import { tinykeys } from "tinykeys"
import type { KeyBindingMap } from "tinykeys"
import { createEditorHotkeys, HOTKEY_IDS } from "./hotkeyRegistry"
import {
	focusElement,
	focusWithRing,
	getDeepActiveElement,
	getFocusableElements,
	isTypingTarget,
	runWhenReady,
} from "./focus"
import type { TEcall, TErefWithFurniture, TFurniture } from "types/elevationReferences.type"
import type { TViewerMode } from "store/viewer.store"

// Only the panel container is needed; typed structurally so the caller can pass its richer
// RootEntry refs without coupling this hook to that type.
type TPanelEntryRef = MutableRefObject<{ container: HTMLElement } | null>

type TUseEditorHotkeysParams = {
	mode: TViewerMode
	isInitialized: boolean
	isModalOpen: boolean
	isPanelOpen: boolean
	isReferencePanelOpen: boolean
	openPanel: () => void
	closePanel: () => void
	// Closes the right (reference) sidebar. Used by the Escape hotkey.
	closeReferencePanel: () => void
	leftPanelEntryRef: TPanelEntryRef
	rightPanelEntryRef: TPanelEntryRef
	// The current right-panel selection. Used only as effect dependencies so focus moves again
	// when Enter activates a different item while the right sidebar is already open.
	rightPanelMode: "eref" | "furniture" | "ecall" | null
	selectedEref: TErefWithFurniture | undefined
	selectedFurniture: TFurniture | undefined
	selectedEcall: TEcall | undefined
	// Right-sidebar navigation actions (REQ-360, tasks 2 & 3). Each performs its own scoping /
	// no-op check and returns whether it actually navigated, so this hook only pulls keyboard
	// focus into the reopened sidebar when a move happened. Optional so scopes can be wired
	// incrementally.
	onNavigateNextEref?: () => boolean
	onNavigatePrevEref?: () => boolean
	onNavigateFurnitureToEref?: () => boolean
}

// Shortcuts whose handler must run even while a text field is focused (the handler itself decides
// what to do from a field). See the dispatcher for the rationale per id.
const TYPING_GATE_BYPASS_IDS = new Set<string>([
	HOTKEY_IDS.cycleFocusForward,
	HOTKEY_IDS.cycleFocusBackward,
	HOTKEY_IDS.closeRightSidebar,
	HOTKEY_IDS.navigateNextEref,
	HOTKEY_IDS.navigatePrevEref,
	HOTKEY_IDS.navigateFurnitureToEref,
])

// Circular Tab focus trap within a container: moves focus to the next/previous focusable inside
// `content`, but only while focus already sits inside `root`. Returns whether it handled the event
// (so a shared Tab handler can try one sidebar, then the other). Shared by the left and right
// sidebar traps — the left passes a content region narrower than its root, the right passes the
// whole panel as both. `useRing` adds our custom keyboard ring (left sidebar rows have no native
// focus style); the right sidebar passes false so form controls show only their own AntD focus
// state — otherwise our ring doubles up with AntD's border/box-shadow.
const cycleFocusWithin = (
	event: KeyboardEvent,
	direction: 1 | -1,
	root: HTMLElement | null,
	content: HTMLElement | null,
	useRing = true,
): boolean => {
	const active = getDeepActiveElement()
	if (!root || !content || !active || !root.contains(active)) return false
	const focusables = getFocusableElements(content)
	if (focusables.length === 0) return false
	event.preventDefault()
	const currentIndex = focusables.indexOf(active)
	const nextIndex =
		currentIndex === -1
			? direction === 1
				? 0
				: focusables.length - 1
			: (currentIndex + direction + focusables.length) % focusables.length
	const next = focusables[nextIndex]
	if (useRing) focusWithRing(next)
	else focusElement(next)
	return true
}

// Keyboard hotkeys for the QuotationEditor (REQ-360). Owns the tinykeys dispatcher, the handlers
// (Alt+1/Alt+3 focus, Tab-trap in either sidebar, Enter activation, Alt+Arrow navigation, Escape
// close), and the "focus the panel once it has mounted" effects. Kept beside the hotkey registry
// so every shortcut lives in one place. Purely side-effectful — see useQuotationViewer for the
// call site.
export const useEditorHotkeys = ({
	mode,
	isInitialized,
	isModalOpen,
	isPanelOpen,
	isReferencePanelOpen,
	openPanel,
	closePanel,
	closeReferencePanel,
	leftPanelEntryRef,
	rightPanelEntryRef,
	rightPanelMode,
	selectedEref,
	selectedFurniture,
	selectedEcall,
	onNavigateNextEref,
	onNavigatePrevEref,
	onNavigateFurnitureToEref,
}: TUseEditorHotkeysParams): void => {
	// Latest panel-open flags, readable inside hotkey handlers without re-binding them.
	const isPanelOpenRef = useRef(isPanelOpen)
	isPanelOpenRef.current = isPanelOpen
	const isReferencePanelOpenRef = useRef(isReferencePanelOpen)
	isReferencePanelOpenRef.current = isReferencePanelOpen
	// Set when a hotkey opens a panel and focus must move there once it has mounted. The
	// focus() call has to wait for the panel render (Apryse mounts it on openElements), so
	// it runs from a useEffect keyed on the open flag rather than inline.
	const pendingLeftFocusRef = useRef(false)
	const pendingRightFocusRef = useRef(false)
	// Cancels the in-flight Alt+3 focus retry loop, so rapid presses don't stack RAF loops.
	const focusRightSidebarCancelRef = useRef<(() => void) | null>(null)

	// The whole sidebar (header + list) is the focus target and the "sidebar-focused" ring host, so
	// the focused state covers the entire panel.
	const getSidebarRoot = useCallback((): HTMLElement | null => {
		const container = leftPanelEntryRef.current?.container
		if (!container) return null
		return container.querySelector<HTMLElement>("[data-hotkey-sidebar]") ?? container
	}, [leftPanelEntryRef])

	// Tab cycling is scoped to the content list only, so the header's close button isn't a stop
	// (it stays mouse-reachable; Escape closes the panel in the follow-up task).
	const getContentRegion = useCallback((): HTMLElement | null => {
		const container = leftPanelEntryRef.current?.container
		if (!container) return null
		return container.querySelector<HTMLElement>("[data-hotkey-content]") ?? container
	}, [leftPanelEntryRef])

	// The right sidebar's focus target and "sidebar-focused" ring host (mirrors getSidebarRoot). Each
	// right-sidebar component tags its root with data-hotkey-sidebar; the Tab trap cycles the whole
	// root, minus the [data-hotkey-skip] header (nav + close buttons) filtered out by
	// getFocusableElements.
	const getRightSidebarRoot = useCallback((): HTMLElement | null => {
		const container = rightPanelEntryRef.current?.container
		if (!container) return null
		return container.querySelector<HTMLElement>("[data-hotkey-sidebar]") ?? container
	}, [rightPanelEntryRef])

	// Move keyboard focus into the left sidebar's top element: open the panel first if it is
	// closed (focus lands once it mounts, via the pending-focus effect), otherwise focus the panel
	// root now. Shared by Alt+1 and by Escape's "return focus to the left sidebar" step.
	const focusLeftSidebarPanel = useCallback(() => {
		if (!isPanelOpenRef.current) {
			pendingLeftFocusRef.current = true
			openPanel()
			return
		}
		const root = getSidebarRoot()
		if (root) requestAnimationFrame(() => focusElement(root))
	}, [openPanel, getSidebarRoot])

	// mod+1 (Alt+1, see hotkeyRegistry) toggles the left sidebar:
	//   - closed        → open it and focus the panel (the first Tab then enters the list)
	//   - open, focused → close it
	//   - open, blurred → move focus into the panel
	const focusLeftSidebar = useCallback(
		(event: KeyboardEvent) => {
			event.preventDefault()
			if (isPanelOpenRef.current) {
				const root = getSidebarRoot()
				const active = getDeepActiveElement()
				if (root && active && root.contains(active)) {
					closePanel()
					return
				}
			}
			focusLeftSidebarPanel()
		},
		[closePanel, getSidebarRoot, focusLeftSidebarPanel],
	)

	// mod+3 (Alt+3, see hotkeyRegistry): focus the right sidebar's root so the whole panel shows the
	// "sidebar-focused" ring (mirrors Alt+1 on the left; the first Tab then enters the controls).
	// Contextual — does nothing when the right sidebar is closed (there is no eref/object/ecall to
	// edit, so no meaningful target). runWhenReady covers the frame where the panel exists but its
	// root has not yet rendered.
	const focusRightSidebar = useCallback(
		(event: KeyboardEvent) => {
			event.preventDefault()
			if (!isReferencePanelOpenRef.current) return
			// Cancel any previous still-retrying loop first so repeated Alt+3 presses don't stack.
			focusRightSidebarCancelRef.current?.()
			focusRightSidebarCancelRef.current = runWhenReady(() => {
				const root = getRightSidebarRoot()
				if (!root || root.getClientRects().length === 0) return false
				focusElement(root)
				return getDeepActiveElement() === root
			})
		},
		[getRightSidebarRoot],
	)

	// Tab / Shift+Tab: a circular focus trap. Tries the left sidebar's list first, then the whole
	// right sidebar — whichever currently holds focus. Only intercepts when focus is inside one of
	// them; otherwise Tab keeps its default behavior. When a panel root itself holds focus (just
	// opened via hotkey, tabIndex -1 so not in the list), Tab enters at the first element and
	// Shift+Tab at the last.
	const cycleSidebarFocus = useCallback(
		(event: KeyboardEvent, direction: 1 | -1) => {
			if (cycleFocusWithin(event, direction, getSidebarRoot(), getContentRegion())) return
			// The right sidebar has no separate content region — the whole root is the trap, minus the
			// [data-hotkey-skip] header (nav + close), which getFocusableElements filters out. useRing
			// is false so its AntD form controls keep their own focus state instead of doubling with ours.
			const rightRoot = getRightSidebarRoot()
			cycleFocusWithin(event, direction, rightRoot, rightRoot, false)
		},
		[getSidebarRoot, getContentRegion, getRightSidebarRoot],
	)

	// Escape: while the right sidebar is open, close it and return focus to the left sidebar's top
	// element. Keyed on the panel being open rather than on focus location — the sidebar may have
	// been opened by a mouse click on an annotation, leaving focus in the PDF; Escape must still
	// close the panel then. A focused text field handles its own Escape first (during its React-tree
	// bubble, before this window-level handler), so an open autocomplete still clears first. Escape
	// never closes the editor itself: the AntModal's native keyboard-close is disabled and there is
	// no fall-through, so with the right sidebar closed Escape is a no-op.
	const closeRightSidebar = useCallback(
		(event: KeyboardEvent) => {
			if (!isReferencePanelOpenRef.current) return
			event.preventDefault()
			closeReferencePanel()
			focusLeftSidebarPanel()
		},
		[closeReferencePanel, focusLeftSidebarPanel],
	)

	// Enter: activate the focused eref / furniture row (same as clicking it) and move focus
	// into the right sidebar once it opens. Reuses the row's click handler so selection logic
	// stays in one place; preventDefault stops a native <button> from firing a second click.
	const activateFocusedItem = useCallback(
		(event: KeyboardEvent) => {
			const root = getSidebarRoot()
			const active = getDeepActiveElement()
			const item = active?.closest<HTMLElement>("[data-hotkey-item]")
			if (!root || !item || !root.contains(item)) return
			event.preventDefault()
			pendingRightFocusRef.current = true
			item.click()
		},
		[getSidebarRoot],
	)

	// Alt+Arrow right-sidebar navigation (next/prev eref, furniture → parent eref). The action
	// owns its own scoping and returns whether it moved; we only queue the focus-into-sidebar when
	// it did, so a scoped no-op doesn't leave a stale pending flag that a later mouse selection
	// would hijack. preventDefault runs UNCONDITIONALLY — including from a text field — because
	// Alt+←/→ is the browser Back/Forward shortcut and must never fire while the editor is open;
	// these combos therefore bypass the dispatcher's typing gate. Navigation itself still stays
	// inert while typing (the field keeps the keystroke).
	const navigateRightSidebar = useCallback((event: KeyboardEvent, action: (() => boolean) | undefined) => {
		event.preventDefault()
		const target = (event.composedPath?.()[0] as EventTarget | undefined) ?? event.target
		if (isTypingTarget(target)) return
		if (action?.()) pendingRightFocusRef.current = true
	}, [])

	// The editor's single hotkey registry (REQ-360 source of truth, also feeds REQ-359's help
	// panel). Stored in a ref so the dispatcher effect can bind once without re-running when the
	// memoized handlers' identities change.
	const editorHotkeys = useMemo(
		() =>
			createEditorHotkeys({
				[HOTKEY_IDS.focusLeftSidebar]: focusLeftSidebar,
				[HOTKEY_IDS.focusRightSidebar]: focusRightSidebar,
				[HOTKEY_IDS.cycleFocusForward]: (event) => cycleSidebarFocus(event, 1),
				[HOTKEY_IDS.cycleFocusBackward]: (event) => cycleSidebarFocus(event, -1),
				[HOTKEY_IDS.activateItem]: activateFocusedItem,
				[HOTKEY_IDS.navigateNextEref]: (event) => navigateRightSidebar(event, onNavigateNextEref),
				[HOTKEY_IDS.navigatePrevEref]: (event) => navigateRightSidebar(event, onNavigatePrevEref),
				[HOTKEY_IDS.navigateFurnitureToEref]: (event) => navigateRightSidebar(event, onNavigateFurnitureToEref),
				[HOTKEY_IDS.closeRightSidebar]: closeRightSidebar,
			}),
		[
			focusLeftSidebar,
			focusRightSidebar,
			cycleSidebarFocus,
			activateFocusedItem,
			navigateRightSidebar,
			onNavigateNextEref,
			onNavigatePrevEref,
			onNavigateFurnitureToEref,
			closeRightSidebar,
		],
	)
	const editorHotkeysRef = useRef(editorHotkeys)
	editorHotkeysRef.current = editorHotkeys

	// Bind the editor hotkeys for the lifetime of an open editor session. tinykeys listens on
	// window: the viewer is a shadow-DOM web component (no iframe), so composed KeyboardEvents
	// from inside the PDF area bubble to window — one target covers both sidebar and PDF focus.
	// Handlers read live state via refs, so the binding never needs to re-run on state changes.
	useEffect(() => {
		if (mode !== "editor" || !isInitialized || !isModalOpen) return
		const bindings: KeyBindingMap = {}
		for (const hotkey of editorHotkeysRef.current) {
			// A few shortcuts must reach their handler even from a text field: Tab cycling (circulate
			// within the trap), Escape (close the right sidebar; its handler does its own typing-aware
			// check for the editor-close fall-through), and the Alt+Arrow nav combos (their handler
			// preventDefaults browser Back/Forward, then no-ops the navigation while typing). Every
			// other shortcut is inert while typing.
			const bypassTypingGate = TYPING_GATE_BYPASS_IDS.has(hotkey.id)
			bindings[hotkey.combo] = (event) => {
				// composedPath()[0] is the real target (event.target is retargeted to the shadow host
				// for events originating inside the viewer).
				const target = (event.composedPath?.()[0] as EventTarget | undefined) ?? event.target
				if (!bypassTypingGate && isTypingTarget(target)) return
				hotkey.handler(event)
			}
		}
		return tinykeys(window, bindings)
	}, [mode, isInitialized, isModalOpen])

	// After mod+1 opens a hidden left sidebar, focus the panel itself (sidebar-focused state) once
	// it has mounted. Apryse mounts the panel asynchronously, so the content region may not exist
	// on the first frame — retry until it does. The user's first Tab then enters the list.
	useEffect(() => {
		if (!isPanelOpen || !pendingLeftFocusRef.current) return
		const cancel = runWhenReady(() => {
			const root = leftPanelEntryRef.current?.container?.querySelector<HTMLElement>("[data-hotkey-sidebar]")
			// On reopen Apryse re-shows the panel a frame later, so the root can exist while still
			// hidden — focus() then no-ops. Wait until it is visible and focus actually lands before
			// stopping, otherwise the ring wouldn't appear until a second hotkey press.
			if (!root || root.getClientRects().length === 0) return false
			focusElement(root)
			if (getDeepActiveElement() !== root) return false
			pendingLeftFocusRef.current = false
			return true
		})
		// When this open cycle ends (panel closed, or the retry gave up because the panel never
		// became visible), drop any unfulfilled pending focus so a later non-hotkey (mouse) open
		// doesn't inherit it and steal focus into the sidebar.
		return () => {
			cancel()
			pendingLeftFocusRef.current = false
		}
	}, [isPanelOpen, leftPanelEntryRef])

	// After Enter activates a row (or Alt+Arrow navigates), move focus into the right sidebar once it
	// has rendered the selected item. Keyed on the selection too, so activating a different item
	// re-focuses. NOTE: unlike the left effect we must NOT clear the pending flag on plain cleanup —
	// these deps change on every navigation, and the cleanup runs before the next run reads the flag,
	// so clearing here would swallow the focus move. Instead the flag is cleared on success (in the
	// attempt) or via onGiveUp if the sidebar never renders a focusable, so a never-fulfilled request
	// can't leak into a later mouse-driven open.
	useEffect(() => {
		if (!isReferencePanelOpen || !pendingRightFocusRef.current) return
		// Same async-mount race as the left panel: retry until the right sidebar's root has rendered
		// and is visible, then focus it so the whole panel shows the "sidebar-focused" ring.
		return runWhenReady(
			() => {
				const root = getRightSidebarRoot()
				if (!root || root.getClientRects().length === 0) return false
				focusElement(root)
				if (getDeepActiveElement() !== root) return false
				pendingRightFocusRef.current = false
				return true
			},
			undefined,
			() => {
				pendingRightFocusRef.current = false
			},
		)
	}, [isReferencePanelOpen, rightPanelMode, selectedEref, selectedFurniture, selectedEcall, getRightSidebarRoot])
}
